<template>
  <b-card no-body class="person-header">
    <b-card-body>
      <b-row>
        <b-col cols="12" md="3" class="text-center mb-2 mb-md-0">
          <b-img
            v-if="mainPhoto"
            :src="mainPhoto"
            alt="Фото"
            fluid
            rounded
            class="person-header__photo"
          />
          <div v-else class="person-header__photo-empty text-muted">
            <feather-icon icon="UserIcon" size="48" />
          </div>
        </b-col>

        <b-col cols="12" md="9">
          <div class="d-flex flex-wrap align-items-start justify-content-between">
            <div class="mb-1">
              <h2 class="mb-0 d-flex align-items-center">
                {{ person.name_ru }}
                <feather-icon
                  v-if="canManage || bestValue"
                  v-b-tooltip.hover
                  :title="canManage ? (bestValue ? 'Убрать из best' : 'Добавить в best') : 'Best'"
                  icon="StarIcon"
                  size="22"
                  class="ml-50 person-header__star"
                  :class="{ 'person-header__star--active': bestValue, 'person-header__star--clickable': canManage && !savingBest }"
                  @click="toggleBest"
                />
              </h2>
              <div v-if="person.name_en" class="text-muted">{{ person.name_en }}</div>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                v-if="canManage"
                variant="outline-primary"
                size="sm"
                :disabled="syncing"
                @click="sync"
              >
                <b-spinner v-if="syncing" small class="mr-50" />
                <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
                Обновить
              </b-button>
            </div>
          </div>

          <div class="mb-1">
            <b-badge
              v-for="profession in professionsList"
              :key="profession"
              variant="light-primary"
              class="mr-50 mb-50"
            >
              {{ profession }}
            </b-badge>
          </div>

          <table class="person-header__facts">
            <tr v-if="person.birthday">
              <th>Рождение</th>
              <td>
                {{ formatDate(person.birthday) }}
                <span v-if="person.age && !person.death" class="text-muted">({{ person.age }} {{ yearsWord(person.age) }})</span>
                <span v-if="person.birthplace">, {{ person.birthplace }}</span>
              </td>
            </tr>
            <tr v-if="person.death">
              <th>Смерть</th>
              <td>
                {{ formatDate(person.death) }}
                <span v-if="person.age" class="text-muted">({{ person.age }} {{ yearsWord(person.age) }})</span>
                <span v-if="person.deathplace">, {{ person.deathplace }}</span>
              </td>
            </tr>
            <tr v-if="person.growth">
              <th>Рост</th>
              <td>{{ person.growth }} см</td>
            </tr>
            <tr v-if="person.sex">
              <th>Пол</th>
              <td>{{ person.sex === 'MALE' ? 'мужской' : 'женский' }}</td>
            </tr>
            <tr v-if="person.awards_count">
              <th>Награды</th>
              <td>{{ person.awards_count }}</td>
            </tr>
            <tr v-if="person.spouses.length">
              <th>Супруги</th>
              <td>
                <div v-for="spouse in person.spouses" :key="spouse.kp_id || spouse.name">
                  <b-link v-if="spouse.person_id" :to="{ name: 'person-view', params: { id: spouse.person_id } }">
                    {{ spouse.name }}
                  </b-link>
                  <span v-else>{{ spouse.name }}</span>
                  <span class="text-muted">
                    <template v-if="spouse.relation"> — {{ spouse.relation }}</template>
                    <template v-if="spouse.divorced">, развод</template>
                    <template v-if="spouse.children">, детей: {{ spouse.children }}</template>
                  </span>
                </div>
              </td>
            </tr>
          </table>

          <div class="mt-1">
            <b-button
              v-for="link in externalLinks"
              :key="link.title"
              :href="link.url"
              target="_blank"
              variant="outline-secondary"
              size="sm"
              class="mr-50 mb-50"
            >
              {{ link.title }}
            </b-button>
          </div>

          <small v-if="person.kp_synced_at" class="text-muted">
            Обновлено {{ formatDateTime(person.kp_synced_at) }}
          </small>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BImg, BBadge, BButton, BLink, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BImg, BBadge, BButton, BLink, BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    person: { type: Object, required: true },
  },
  data() {
    return {
      bestValue: Boolean(this.person.best),
      savingBest: false,
      syncing: false,
    }
  },
  watch: {
    'person.best'(value) {
      this.bestValue = Boolean(value)
    },
  },
  computed: {
    canManage() {
      return this.$can('manage', 'db-movies')
    },
    mainPhoto() {
      return this.person.photo || this.person.poster
    },
    professionsList() {
      if (this.person.professions.length) return this.person.professions
      return this.person.profession ? this.person.profession.split(',').map(item => item.trim()) : []
    },
    externalLinks() {
      const links = []
      if (this.person.kp_id) links.push({ title: 'Кинопоиск', url: `https://www.kinopoisk.ru/name/${this.person.kp_id}/` })
      if (this.person.imdb_id) links.push({ title: 'IMDb', url: `https://www.imdb.com/name/${this.person.imdb_id}/` })
      if (this.person.tmdb_id) links.push({ title: 'TMDb', url: `https://www.themoviedb.org/person/${this.person.tmdb_id}` })
      if (this.person.wikidata_id) links.push({ title: 'Wikidata', url: `https://www.wikidata.org/wiki/${this.person.wikidata_id}` })
      if (this.person.instagram) links.push({ title: 'Instagram', url: `https://www.instagram.com/${this.person.instagram}/` })
      return links
    },
  },
  methods: {
    formatDate(value) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    },
    formatDateTime(value) {
      return new Date(value).toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    yearsWord(age) {
      const lastTwo = age % 100
      const last = age % 10
      if (lastTwo >= 11 && lastTwo <= 14) return 'лет'
      if (last === 1) return 'год'
      if (last >= 2 && last <= 4) return 'года'
      return 'лет'
    },
    notify(title, variant, icon) {
      this.$toast({ component: ToastificationContent, props: { title, variant, icon } })
    },
    toggleBest() {
      if (!this.canManage || this.savingBest) return
      const value = !this.bestValue
      this.bestValue = value
      this.savingBest = true
      this.$http.post(`/person/${this.person.id}`, { best: value })
        .then(res => this.$emit('update', res.data.data))
        .catch(() => {
          // Откатываем переключатель, раз сервер значение не принял
          this.bestValue = !value
          this.notify('Не удалось сохранить best', 'danger', 'AlertTriangleIcon')
        })
        .finally(() => { this.savingBest = false })
    },
    sync() {
      this.syncing = true
      this.$http.post(`/person/${this.person.id}/sync`)
        .then(res => {
          this.$emit('update', res.data.data)
          this.notify('Данные персоны обновлены', 'success', 'CheckIcon')
        })
        .catch(() => this.notify('Не удалось обновить персону', 'danger', 'AlertTriangleIcon'))
        .finally(() => { this.syncing = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.person-header__star {
  color: #b9b9c3;
}
.person-header__star--active {
  color: #ff9f43;
  fill: currentColor;
}
.person-header__star--clickable {
  cursor: pointer;
}
.person-header__photo {
  max-height: 360px;
  object-fit: cover;
}
.person-header__photo-empty {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed currentColor;
  border-radius: 0.428rem;
}
.person-header__facts th {
  padding: 0 1rem 0.35rem 0;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: top;
}
.person-header__facts td {
  padding-bottom: 0.35rem;
}
</style>
